/**
 * Renaming and moving a file or a folder in the Code tree (RepositoryTree): the name check, and where the paths that
 * were under the old one are now.
 */

/** The folder a tree path is in ('' = the tree's root). */
export const parentOf = (path) => (path && path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '');

/** The last segment of a tree path. */
export const baseName = (path) => String(path || '').split('/').pop();

/** `folder/name`, or `name` at the root. */
export const childPath = (folder, name) => (folder ? `${folder}/${name}` : name);

/**
 * Why `name` cannot be the new name of an entry in a folder holding `siblings` (the names there, the entry's own
 * included), or null when it can. `current`: the entry's name now (keeping it is not a clash).
 */
export function entryNameProblem(name, {siblings = [], current = null} = {}) {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) { return 'Type a name.'; }
  if (trimmed.includes('/') || trimmed.includes('\\')) { return 'A name cannot contain "/" or "\\".'; }
  if (trimmed === '.' || trimmed === '..') { return `"${trimmed}" cannot be a name.`; }
  if (trimmed !== current && siblings.includes(trimmed)) { return `"${trimmed}" is already in this folder.`; }
  return null;
}

/** Where `path` is after `from` became `to`: `to` itself, the same place under `to`, or null when it was not there. */
export function movedPath(path, from, to) {
  if (!path || !from) { return null; }
  if (path === from) { return to; }
  if (path.startsWith(`${from}/`)) { return `${to}${path.slice(from.length)}`; }
  return null;
}

/** A set of paths (the folders open in the tree) after `from` became `to`. */
export function movePaths(paths, from, to) {
  return new Set([...paths].map((path) => movedPath(path, from, to) ?? path));
}

/**
 * The folders an entry at `fromPath` can be moved into, from `folders` ('' = the root, always offered): not the
 * entry itself nor anything inside it. The folder it is in now stays in the list (the picker shows it as "here now").
 */
export function moveTargets(folders, fromPath) {
  const all = new Set(['', ...folders]);
  return [...all]
    .filter((folder) => folder !== fromPath && !folder.startsWith(`${fromPath}/`))
    .sort((a, b) => a.localeCompare(b));
}

/**
 * Why `input` cannot be the name of a new file or folder created in a folder, or null when it can. `allowNested`:
 * `sub/dir/name.js` is accepted (the missing folders are created too), else a "/" is refused. `entryAt(path)`: what is
 * at a path relative to that folder now, 'file', 'folder' or null, for "already there" and "a file in the way".
 */
export function newEntryProblem(input, {allowNested = false, entryAt = () => null} = {}) {
  const trimmed = String(input ?? '').trim();
  if (!trimmed) { return 'Type a name.'; }
  if (trimmed.includes('\\')) { return 'A name cannot contain "\\".'; }
  const parts = trimmed.split('/');
  if (parts.length > 1 && !allowNested) { return 'A name cannot contain "/". Create the folder first, then this inside it.'; }
  if (parts.some((part) => !part)) { return 'A path cannot start or end with "/" or have "//" in it.'; }
  const odd = parts.find((part) => part === '.' || part === '..');
  if (odd) { return `"${odd}" cannot be a name.`; }
  for (let index = 1; index < parts.length; index += 1) {
    const folder = parts.slice(0, index).join('/');
    if (entryAt(folder) === 'file') { return `"${folder}" is a file, not a folder.`; }
  }
  if (entryAt(trimmed)) { return `"${trimmed}" is already in this folder.`; }
  return null;
}
